import { useMemo, useState, useEffect } from "react";
import Select from "../../../shared/Select";

/**
 * Custom mileage selector component for manifest table
 * Combobox that allows selecting from route mileage options or typing custom value
 * @param {Object} props - Component props
 * @param {Object} props.row - Table row data containing route information
 * @param {*} props.value - Current value (mileage number or route object)
 * @param {Function} props.onChange - Change handler
 */
const MileageSelector = ({ row, value, onChange }) => {
  const route = row?.original?.route;
  const leg = row?.original;

  // Determine current mileage value from leg data
  const currentMileage = useMemo(() => {
    if (leg?.useCustomManifestMileage && leg?.customManifestMileage != null) {
      return leg.customManifestMileage;
    }
    if (route) {
      return route.mileage || route.defaultMileage || 0;
    }
    return 0;
  }, [leg, route]);

  // Build mileage options from route data
  const mileageOptions = useMemo(() => {
    const options = [];
    const seenValues = new Set(); // Track values to avoid duplicates

    // Default Mileage (preferred: defaultMileage, fallback: mileage)
    const defaultMileage = route?.defaultMileage ?? route?.mileage;
    if (defaultMileage != null && defaultMileage > 0) {
      options.push({
        id: "defaultMileage",
        label: `Default Mileage (${defaultMileage} mi)`,
        value: defaultMileage,
      });
      seenValues.add(defaultMileage);
    }

    // Mileage (only if different from defaultMileage)
    if (
      route?.mileage != null &&
      route.mileage > 0 &&
      route.mileage !== defaultMileage
    ) {
      options.push({
        id: "mileage",
        label: `Billed Mileage (${route.mileage} mi)`,
        value: route.mileage,
      });
      seenValues.add(route.mileage);
    }

    // Custom Mileage (from route) - only if different from defaultMileage
    if (
      route?.customMileage != null &&
      route.customMileage > 0 &&
      !seenValues.has(route.customMileage)
    ) {
      options.push({
        id: "customMileage",
        label: `Custom Mileage (${route.customMileage} mi)`,
        value: route.customMileage,
      });
      seenValues.add(route.customMileage);
    }

    // Billed Miles - only if different from other values
    if (
      route?.billedMiles != null &&
      route.billedMiles > 0 &&
      !seenValues.has(route.billedMiles)
    ) {
      options.push({
        id: "billedMiles",
        label: `Billed Miles (${route.billedMiles} mi)`,
        value: route.billedMiles,
      });
      seenValues.add(route.billedMiles);
    }

    // Add custom manifest mileage if it's set and doesn't match any route option
    if (
      leg?.useCustomManifestMileage &&
      leg?.customManifestMileage != null &&
      leg.customManifestMileage > 0 &&
      !seenValues.has(leg.customManifestMileage)
    ) {
      options.push({
        id: "customManifestMileage",
        label: `Custom Manifest Mileage (${leg.customManifestMileage} mi)`,
        value: leg.customManifestMileage,
      });
      seenValues.add(leg.customManifestMileage);
    }

    return options;
  }, [route, leg]);

  // Get current display value (number or formatted string)
  const [displayValue, setDisplayValue] = useState(() => {
    if (leg?.useCustomManifestMileage && leg?.customManifestMileage != null) {
      return leg.customManifestMileage.toString();
    }
    return currentMileage.toString();
  });

  useEffect(() => {
    // Update display value when leg data changes
    if (leg?.useCustomManifestMileage && leg?.customManifestMileage != null) {
      setDisplayValue(leg.customManifestMileage.toString());
    } else {
      setDisplayValue(currentMileage.toString());
    }
  }, [leg, currentMileage]);

  const handleChange = (e) => {
    // This is called when user selects an option from dropdown or when value changes
    const inputValue = e.value || e.target?.value || displayValue;
    const stringValue =
      typeof inputValue === "string" ? inputValue : inputValue.toString();

    // Check if the value matches any route option exactly
    const numValue = parseFloat(stringValue);
    const matchingOption = mileageOptions.find(
      (opt) => opt.value === numValue || opt.value.toString() === stringValue
    );

    if (matchingOption) {
      // User selected or typed an option (route option or custom manifest mileage)
      setDisplayValue(matchingOption.value.toString());
      const mileageData = {
        value: matchingOption.value,
        isRouteMileage: matchingOption.id !== "customManifestMileage",
        mileageType:
          matchingOption.id === "customManifestMileage"
            ? "customInput"
            : matchingOption.id,
      };
      onChange({ value: mileageData });
    } else if (
      !isNaN(numValue) &&
      numValue >= 0 &&
      stringValue.trim().length > 0
    ) {
      // Custom typed value (numeric and valid)
      setDisplayValue(stringValue);
      const mileageData = {
        value: numValue,
        isRouteMileage: false,
        mileageType: "customInput",
      };
      onChange({ value: mileageData });
    }
  };

  const handleFullTextSearch = (searchValue) => {
    // This is called when user types in the searchable input
    setDisplayValue(searchValue);

    // Filter options for display
    const filteredOptions = mileageOptions.filter((option) => {
      const value = searchValue.toUpperCase();
      const optionValue = option.value
        ? option.value.toString().toUpperCase()
        : "";
      const optionLabel = option.label ? option.label.toUpperCase() : "";

      if (!value) return true;
      if (optionValue.includes(value)) return true;
      if (optionLabel.includes(value)) return true;
      return false;
    });

    // Check if typed value matches an option exactly
    const numValue = parseFloat(searchValue);
    const matchingOption = mileageOptions.find(
      (opt) => opt.value === numValue || opt.value.toString() === searchValue
    );

    if (matchingOption) {
      // Typed value matches an option (route option or custom manifest mileage) - update immediately
      const mileageData = {
        value: matchingOption.value,
        isRouteMileage: matchingOption.id !== "customManifestMileage",
        mileageType:
          matchingOption.id === "customManifestMileage"
            ? "customInput"
            : matchingOption.id,
      };
      onChange({ value: mileageData });
    } else if (
      !isNaN(numValue) &&
      numValue >= 0 &&
      searchValue.trim().length > 0
    ) {
      // Custom typed value (numeric and valid) - update immediately
      const mileageData = {
        value: numValue,
        isRouteMileage: false,
        mileageType: "customInput",
      };
      onChange({ value: mileageData });
    }

    return filteredOptions;
  };

  return (
    <div className="min-w-[200px]">
      <Select
        size="sm"
        value={displayValue}
        onChange={handleChange}
        search={true}
        fullTextSearch={handleFullTextSearch}
        options={mileageOptions}
        placeholder="Select or type mileage"
      />
    </div>
  );
};

export default MileageSelector;
